if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.updateMediaGrouping();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      elementMatchesVariant(element, variantId) {
        if (!variantId) return false;
        const idsAttr = element.getAttribute("data-variant-ids")?.trim?.();
        if (idsAttr) {
          const idList = idsAttr.split(/\s+/).filter(Boolean);
          return idList.includes(variantId);
        }
        const singleId =
          element.getAttribute("data-variant-id")?.trim?.() || "";
        return singleId === variantId;
      }

      getMediaPositionInVariantGroup() {
        const ul = this.querySelector("media-gallery ul");
        const activeSlide = ul?.querySelector(".product__media-item.is-active");
        if (!activeSlide) return 0;
        const activeVariantId = this.getActiveVariantId();
        const visibleSlides = Array.from(
          ul.querySelectorAll(".product__media-item[data-media-id]")
        ).filter((li) => !li.classList.contains("hide-image"));
        let scopedSlides = visibleSlides;
        if (activeVariantId) {
          const variantSlides = visibleSlides.filter((li) =>
            this.elementMatchesVariant(li, activeVariantId)
          );
          if (variantSlides.length) scopedSlides = variantSlides;
        }
        const index = scopedSlides.indexOf(activeSlide);
        return index >= 0 ? index : 0;
      }

      getActiveVariantId() {
        const fromProductForm =
          this.productForm?.variantIdInput?.value?.trim?.() || "";
        if (fromProductForm) return fromProductForm;

        const hiddenInput = this.querySelector(
          `#product-form-${this.dataset.section} input[name="id"]`
        );
        const fromHiddenInput = hiddenInput?.value?.trim?.() || "";
        if (fromHiddenInput) return fromHiddenInput;

        const selectedVariant = this.getSelectedVariant(this);
        if (selectedVariant?.id) return String(selectedVariant.id);

        const activeSlideVariantId =
          this.querySelector(
            "media-gallery .product__media-item.is-active[data-variant-id]"
          )
            ?.getAttribute("data-variant-id")
            ?.trim?.() || "";
        if (activeSlideVariantId) return activeSlideVariantId;

        const firstVariantMediaId =
          this.querySelector(
            "media-gallery .product__media-item[data-variant-id]"
          )
            ?.getAttribute("data-variant-id")
            ?.trim?.() || "";
        return firstVariantMediaId;
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        const mediaPositionInVariant = this.getMediaPositionInVariantGroup();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, mediaPositionInVariant),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            if (targetId) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      selectVariantById(variantId) {
        if (!variantId) return;
        const currentVariantId =
          this.productForm?.variantIdInput?.value?.trim?.() || "";
        if (String(variantId) === currentVariantId) return;

        this.resetProductFormState();
        const productUrl = this.dataset.url;
        const requestUrl = `${productUrl}?variant=${variantId}&section_id=${this.dataset.section}`;
        this.renderProductInfo({
          requestUrl,
          callback: this.handleUpdateProductInfoFromMediaClick(productUrl),
        });
      }

      handleUpdateProductInfoFromMediaClick(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html, { inPlace: true });
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html, options = {}) {
        const variantSelects = html.querySelector("variant-selects");
        if (!variantSelects) return;
        if (options.inPlace) {
          this.preProcessHtmlCallbacks?.forEach((cb) => cb(variantSelects));
          HTMLUpdateUtility.setInnerHTML(
            this.variantSelectors,
            variantSelects.innerHTML
          );
          return;
        }
        HTMLUpdateUtility.viewTransition(
          this.variantSelectors,
          variantSelects,
          this.preProcessHtmlCallbacks
        );
      }

      handleUpdateProductInfo(productUrl, mediaPositionInVariant) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            html,
            variant?.featured_media?.id,
            mediaPositionInVariant
          );
          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId, mediaPositionInVariant) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set active media: keep same position in target variant when possible
        const sectionId = this.dataset.section;
        let activeMediaId = `${sectionId}-${variantFeaturedMediaId}`;
        if (
          mediaPositionInVariant != null &&
          mediaPositionInVariant >= 0 &&
          mediaGallerySource
        ) {
          const featuredSlide = mediaGallerySource.querySelector(
            `li[data-media-id="${activeMediaId}"]`
          );
          const activeVariantId = this.getActiveVariantId();
          const allSlides = Array.from(
            mediaGallerySource.querySelectorAll(
              ".product__media-item[data-media-id]"
            )
          );
          let newVariantSlides = allSlides;
          if (activeVariantId) {
            const variantSlides = allSlides.filter((li) =>
              this.elementMatchesVariant(li, activeVariantId)
            );
            if (variantSlides.length) newVariantSlides = variantSlides;
          }
          const index = Math.min(
            mediaPositionInVariant,
            newVariantSlides.length - 1
          );
          if (index >= 0 && newVariantSlides[index]) {
            activeMediaId = newVariantSlides[index].dataset.mediaId;
          }
        }
        this.updateMediaGrouping();
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          activeMediaId,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
          this.updateMediaGrouping();
        }
      }
      updateMediaGrouping() {
        const mediaGallery = this.querySelector("media-gallery ul");
        if (!mediaGallery) return;

        const groupVariants = mediaGallery.getAttribute("data-group-variants");
        const hasOnlyDefaultVariant = mediaGallery.getAttribute(
          "data-has-only-default-variant"
        );
        const disableImageGrouping = mediaGallery.getAttribute(
          "data-disable-image-grouping"
        );

        if (
          groupVariants === "false" ||
          hasOnlyDefaultVariant === "true" ||
          disableImageGrouping === "true"
        )
          return;

        const mediaItems = Array.from(
          mediaGallery.querySelectorAll(".product__media-item")
        );

        // Build ordered list of variant groups: 1, 2, 3, … (numeric where possible), empty last
        const groupNames = [
          ...new Set(
            mediaItems
              .map((el) => el.getAttribute("data-variant-grouping") ?? "")
              .filter(Boolean)
          ),
        ].sort((a, b) => {
          const numA = parseInt(a, 10);
          const numB = parseInt(b, 10);
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
          if (!Number.isNaN(numA)) return -1;
          if (!Number.isNaN(numB)) return 1;
          return a.localeCompare(b, undefined, { numeric: true });
        });

        const groupOrder = (value) => {
          const v = value ?? "";
          const i = groupNames.indexOf(v);
          return i === -1 ? groupNames.length : i;
        };

        // Sort gallery: Variante 1 zuerst, dann Variante 2, …
        mediaItems.sort(
          (a, b) =>
            groupOrder(a.getAttribute("data-variant-grouping")) -
            groupOrder(b.getAttribute("data-variant-grouping"))
        );
        mediaItems.forEach((el) => mediaGallery.appendChild(el));
        const activeVariantId = this.getActiveVariantId();
        const applyVariantVisibility = (elements) => {
          const items = Array.from(elements);
          if (!items.length) return;

          const hasVariantScopedItem =
            !!activeVariantId &&
            items.some((item) =>
              this.elementMatchesVariant(item, activeVariantId)
            );

          items.forEach((item) => {
            const matchesVariant = this.elementMatchesVariant(
              item,
              activeVariantId
            );
            const hasVariantScope =
              item.getAttribute("data-variant-id")?.trim?.() ||
              item.getAttribute("data-variant-ids")?.trim?.();
            const isVisible = !activeVariantId
              ? true
              : hasVariantScope
              ? matchesVariant
              : !hasVariantScopedItem;
            item.classList.toggle("hide-image", !isVisible);
          });
        };

        applyVariantVisibility(
          mediaGallery.querySelectorAll(".product__media-item")
        );

        // Thumbnail-Liste in derselben Reihenfolge sortieren
        const thumbnailList = this.querySelector(
          "media-gallery ul.thumbnail-list"
        );
        if (thumbnailList) {
          const thumbItems = Array.from(
            thumbnailList.querySelectorAll(".thumbnail-list__item")
          );
          thumbItems.sort(
            (a, b) =>
              groupOrder(a.getAttribute("data-variant-grouping")) -
              groupOrder(b.getAttribute("data-variant-grouping"))
          );
          thumbItems.forEach((el) => thumbnailList.appendChild(el));
          applyVariantVisibility(
            thumbnailList.querySelectorAll(".thumbnail-list__item")
          );
        }

        this.querySelector("media-gallery")?.updateThumbnailSlider?.();

        // Update modal content if it exists
        const modalContent = this.productModal?.querySelector(
          ".product-media-modal__content"
        );
        if (modalContent) {
          applyVariantVisibility(
            modalContent.querySelectorAll(
              ".product__media-item, .thumbnail-list__item"
            )
          );
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
